import api from '../config/api';
import {
  UserGeneratedContentResponse,
  UGCFormState,
  UserGeneratedContentPayload,
  NotificationChannel,
  NotificationChannels,
  ContentVideo,
  MediaGroup,
} from '../models/user-generated-contents/types';

interface UGCApiAttributes {
  id?: number;
  title?: string;
  summary?: string;
  body?: string;
  publication_state?: UGCFormState['publication_state'];
  content_cover?: UGCFormState['content_cover'];
  is_translatable?: boolean;
  is_commentable?: boolean;
  is_shareable?: boolean;
  attachments?: MediaGroup[];
  content_channels?: Array<{ id: number; name: string }>;
  notification_channels?: NotificationChannels;
}

export function normalizeVideos(
  videos: ContentVideo[] | undefined
): ContentVideo[] | undefined {
  return videos?.map(({ transcode_url, ...video }) => ({
    ...video,
    url: transcode_url ?? video.url,
  }));
}

export function normalizeAttachments(
  attachments: MediaGroup[] | undefined
): MediaGroup[] | undefined {
  return attachments?.map((group) => {
    if (group.type === 'videos') {
      return { ...group, items: normalizeVideos(group.items) ?? [] };
    }
    return group;
  });
}

function withProcessedFlag(data: UGCFormState): object {
  if (!data.content_cover?.image) return data;
  const { status } = data.content_cover.image;
  const processed = status === 'processing' || status === 'completed';
  return {
    ...data,
    content_cover: {
      ...data.content_cover,
      image: { ...data.content_cover.image, processed },
    },
  };
}

function toApiPayload(
  data: Partial<UGCFormState>
): Partial<UserGeneratedContentPayload> {
  const {
    audiences,
    notification_settings,
    topic_ids,
    limit_visibility_to_audience,
    ...rest
  } = data;

  const hasAudiences = !!(audiences && audiences.length > 0);
  const ncEnabled =
    hasAudiences && !!notification_settings?.notification_center?.enabled;
  const emailEnabled = hasAudiences && !!notification_settings?.email?.enabled;
  // Selecting Push implies Notification Center:
  const pushEnabled =
    hasAudiences && !!notification_settings?.push_notification?.enabled;
  const ncImpliedByPush = pushEnabled || ncEnabled;

  // Only include notification text for channels that are actually enabled.
  // Sending stale message text for a disabled channel can trip pony's
  // notification validators on publish.
  const channels: NotificationChannel[] = [];
  if (ncImpliedByPush) {
    channels.push({
      type: 'assistant',
      preview_text: notification_settings?.notification_center?.message,
    });
  }
  if (emailEnabled) {
    channels.push({
      type: 'email',
      subject: notification_settings?.email?.subject,
      preview_text: notification_settings?.email?.preview,
    });
  }
  if (pushEnabled) {
    channels.push({
      type: 'push',
      preview_text: notification_settings?.push_notification?.message,
    });
  }
  if (topic_ids && topic_ids.length > 0) {
    channels.push({ type: 'feed' });
  }

  return {
    ...rest,
    topic_ids,
    notification_channels: {
      audiences,
      channels,
      ...(limit_visibility_to_audience !== undefined
        ? { limit_visibility_to_audience }
        : {}),
    },
  };
}

export function fromApiPayload(
  attributes: UGCApiAttributes
): Partial<UGCFormState> {
  const nc = attributes.notification_channels;
  const channels: NotificationChannel[] = nc?.channels ?? [];
  function findChannel<T extends NotificationChannel['type']>(
    type: T
  ): Extract<NotificationChannel, { type: T }> | undefined {
    return channels.find((c) => c.type === type) as
      | Extract<NotificationChannel, { type: T }>
      | undefined;
  }
  const emailChannel = findChannel('email');

  return {
    id: attributes.id,
    title: attributes.title,
    summary: attributes.summary,
    body: attributes.body,
    publication_state: attributes.publication_state,
    content_cover: attributes.content_cover,
    is_translatable: attributes.is_translatable ?? false,
    is_commentable: attributes.is_commentable ?? false,
    is_shareable: attributes.is_shareable ?? false,
    attachments: normalizeAttachments(attributes.attachments),
    topic_ids: (attributes.content_channels ?? []).map((c) => c.id),
    audiences: nc?.audiences ?? [],
    limit_visibility_to_audience: nc?.limit_visibility_to_audience ?? false,
    notification_settings: {
      notification_center: {
        enabled: channels.some((c) => c.type === 'assistant'),
        message: findChannel('assistant')?.preview_text,
      },
      email: {
        enabled: !!emailChannel,
        subject: emailChannel?.subject,
        preview: emailChannel?.preview_text,
      },
      push_notification: {
        enabled: channels.some((c) => c.type === 'push'),
        message: findChannel('push')?.preview_text,
      },
    },
  };
}
export const updateStatusUrlMap = {
  active: 'restore',
  submitted: 'submit',
  archived: 'archive',
} as const;

class UserGeneratedContentService {
  id: number | undefined;

  constructor(id: number | undefined) {
    this.id = id;
  }

  fetch = () => {
    return api.get<UserGeneratedContentResponse>(
      `/user_generated_contents/${this.id}`
    );
  };

  create = (data: UGCFormState) => {
    return api.post<UserGeneratedContentResponse>(
      '/user_generated_contents',
      toApiPayload(withProcessedFlag(data))
    );
  };

  update = (data: Partial<UGCFormState>) => {
    return api.put<UserGeneratedContentResponse>(
      `/user_generated_contents/${this.id}`,
      toApiPayload(withProcessedFlag(data))
    );
  };

  submit = () => {
    return api.post<UserGeneratedContentResponse>(
      `/user_generated_contents/${this.id}/submit`
    );
  };

  copy = () => {
    // Copy re-hosts every source asset synchronously server-side, which runs
    // ~15-26s for posts near the media cap. The default 20s client timeout aborts
    // before Pony finishes, surfacing a false error even though the duplicate is
    // created. Give this one call room to complete (infra already allows it: ALB
    // idle 600s, no mojo->pony timeout); stay under Puma's 60s worker timeout.
    return api.post<UserGeneratedContentResponse>(
      `/user_generated_contents/${this.id}/copy`,
      undefined,
      { timeout: 45_000 }
    );
  };

  archive = () => {
    return api.put<UserGeneratedContentResponse>(
      `/user_generated_contents/${this.id}`,
      { publication_state: 'archived' }
    );
  };
}

export default UserGeneratedContentService;
